import {
  Branded,
  GeckoAssetModelType,
  GeckoComponentCoords,
  GeckoMeasurement,
  GeckoMeasurementJson,
  Section,
  Sectionable,
} from 'types'

export interface RugSectionMinimum {
  thickness: GeckoMeasurement
  location: GeckoComponentCoords
}

export interface RugSectionMinimumRaw {
  thickness: GeckoMeasurementJson
  location: {
    componentID: string
    x: number
    xUnit: string
    y: number
    yUnit: string
  }
}

export interface RugSectionStats {
  section: Section
  minThicknesses: RugSectionMinimum[]
  averageThickness: GeckoMeasurement
  tMin?: GeckoMeasurement
  nominalThickness?: GeckoMeasurement
  maxWallLossPercent?: GeckoMeasurement
}

export interface RugSectionStatsRaw {
  section: SectionRaw
  minThicknesses: RugSectionMinimumRaw[]
  averageThickness: GeckoMeasurementJson
  tMin?: GeckoMeasurementJson
  nominalThickness?: GeckoMeasurementJson
  maxWallLossPercent?: GeckoMeasurementJson
}

export type RugComponentStats = Omit<RugSectionStats, 'section'>

export type RugComponentStatsRaw = Omit<RugSectionStatsRaw, 'section'>

export type ComponentId = Branded<string, 'ComponentId'>

export enum SensorPayloadType {
  THIN = 'SD-Thin',
  THICK = 'SD-Thick',
  TANK = 'SD-Tank',
  UNKNOWN = 'UNKNOWN',
  MIXED = 'MIXED',
}

export interface SensorPayloadMetadata {
  payloadType: SensorPayloadType
  sensorFrequency?: number // in MHz
  sensorDiameter: GeckoMeasurement
  angle: number // in degrees
}

export interface InspectionMetadata {
  inspectionSoftwareVersion?: string
  reported_ascan_count?: number
  measurementModeLabel?: string
  sensorPayloadMetadata?: SensorPayloadMetadata
}

export type SectionRaw = {
  name: string
  min: GeckoMeasurementJson
  max: GeckoMeasurementJson
}

export type SectionableRaw = Omit<Sectionable, 'sections'> & {
  sections: SectionRaw[]
}

export enum ReportMaterialType {
  CARBON_STEEL = 'Carbon Steel',
  STAINLESS_STEEL = 'Stainless Steel',
  HASTELLOY = 'Hastelloy',
  CAST_IRON = 'Cast Iron',
  CARBON_MOLY = 'Carbon - 1/2 Moly',
  LOW_CHROME = 'Low Chrome',
  ALUMINUM = 'Aluminum',
  MONEL = 'Monel',
  TITANIUM = 'Titanium',
  OTHER = 'Other',
}

export enum TankXAxisStartLocation {
  NORTH = 'North (Pos. dir. CW looking Up)',
  VERT = 'Vert. weld left of North Manway (Pos. dir. CW looking Up)',
  ZERO = '0',
}

export enum BoilerXAxisStartLocation {
  NW = 'North West Corner (Tube 1)',
}

export const XAxisStartLocationOptions: Partial<
  Record<GeckoAssetModelType, string[]>
> = {
  [GeckoAssetModelType.VerticalTank]: [
    TankXAxisStartLocation.NORTH,
    TankXAxisStartLocation.VERT,
    TankXAxisStartLocation.ZERO,
  ],
  [GeckoAssetModelType.HorizontalTank]: [
    TankXAxisStartLocation.NORTH,
    TankXAxisStartLocation.VERT,
    TankXAxisStartLocation.ZERO,
  ],
  [GeckoAssetModelType.LegacyBoiler]: [BoilerXAxisStartLocation.NW],
}

export enum TankYAxisStartLocation {
  CW1 = 'CW-1',
  ZERO = '0',
}

export enum BoilerYAxisStartLocation {
  ZERO = "0' Elevation",
  CUSTOM = "<enter elevation>' Elevation",
}

export const YAxisStartLocationOptions: Partial<
  Record<GeckoAssetModelType, string[]>
> = {
  [GeckoAssetModelType.VerticalTank]: [
    TankYAxisStartLocation.CW1,
    TankYAxisStartLocation.ZERO,
  ],
  [GeckoAssetModelType.HorizontalTank]: [
    TankYAxisStartLocation.CW1,
    TankYAxisStartLocation.ZERO,
  ],
  [GeckoAssetModelType.LegacyBoiler]: [
    BoilerYAxisStartLocation.ZERO,
    BoilerYAxisStartLocation.CUSTOM,
  ],
}

export enum ReportedAssetType {
  AST = 'Aboveground Storage Tank',
  TANK = 'Tank',
  VESSEL = 'Vessel',
  DRUM = 'Drum',
  PIPING = 'Piping',
  SDA = 'SDA',
  SILO = 'Silo',
  BOILER = 'Boiler',
}

export enum TankReportCoverageScope {
  FULL_SHELL = '100% of the accessible shell',
  FULL_TANK = '100% of the accessible shell, roof, and annular ring',
  DROPS = 'Four (4) clock position drops of the shell',
  FULL_SHELL_AND_HEADS = '100% of the accessible shell and heads',
}

export enum BoilerReportCoverageScope {
  FULL = '100% of the accessible water walls',
  SPECIFIC_AREA = '100% of the accessible areas of the XXXX and XXXX water walls',
  SPECIFIC_ELEVATION = "100% of the accessible water walls from elevations XXX' to XXX'",
}

export const ReportCoverageScopeOptions: Partial<
  Record<GeckoAssetModelType, string[]>
> = {
  [GeckoAssetModelType.VerticalTank]: [
    TankReportCoverageScope.FULL_SHELL,
    TankReportCoverageScope.FULL_TANK,
    TankReportCoverageScope.DROPS,
    TankReportCoverageScope.FULL_SHELL_AND_HEADS,
  ],
  [GeckoAssetModelType.HorizontalTank]: [
    TankReportCoverageScope.FULL_SHELL,
    TankReportCoverageScope.FULL_TANK,
    TankReportCoverageScope.DROPS,
    TankReportCoverageScope.FULL_SHELL_AND_HEADS,
  ],
  [GeckoAssetModelType.LegacyBoiler]: [
    BoilerReportCoverageScope.FULL,
    BoilerReportCoverageScope.SPECIFIC_AREA,
    BoilerReportCoverageScope.SPECIFIC_ELEVATION,
  ],
}

export enum ReportInspectionSurface {
  ID = 'I.D. Surface',
  OD = 'O.D. Surface',
  FIRESIDE = 'Fireside',
  COLDSIDE = 'Coldside',
  ROOF = 'Roof',
  FLOOR = 'Floor',
  SERVICE_SIDE = 'Service Side',
  NON_SERVICE_SIDE = 'Non-Service Side',
}

export enum ReportSurfaceCondition {
  CLEAN = 'Clean',
  FAIR = 'Fair',
  POOR = 'Poor',
  RUSTED = 'Rusted',
  POWER_BUFFED = 'Power Buffed',
  SAND_BLASTED = 'Sand Blasted',
}

export enum CalibrationBlockType {
  FIVE_STEP = '5-Step Block (0.100"-0.500")',
  TIPSY_BLOCK = 'Tipsy Step Block (1.0"-4.5")',
}

export enum CalibrationStepName {
  THIN = 'Thin',
  THICK = 'Thick',
  VERIFICATION = 'Verification',
}
